// FantasyPage.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Leagues from "./Components/Leagues";
import Teams from "./Components/FantasyTeam";
import {
	createLeague,
	getLeagues,
	getUserLeagues,
} from "../../services/leagueService";
import { getTeams } from "../../services/teamService";
import {
	Container,
	Row,
	Col,
	Button,
	Placeholder,
} from "react-bootstrap";
import Modal from "../common/components/ThemedModal";
import CreateLeagueModal from "./Components/CreateLeagueModal";
import { MdPlaylistAdd } from "react-icons/md";
import { FaUsers } from "react-icons/fa";
import "./Fantasy.css";
import EventCard from "./Components/EventCard";
import Prizes from "./Components/Prizes";
import { Helmet } from "react-helmet";
import FeaturedLeaguesComponent from "./Components/FeaturedLeagues";
import "../HomePage/HomePage.css";
import { FiLogIn } from "react-icons/fi";
import {
	CreateLeague,
	Event,
	initialEvent,
	Series,
	Sport,
	TeamComplete,
	LeagueWithSportsAndSeries,
} from "../../types/model";
import {
	filterEventsBySportAndSeries,
	getEventSeries,
	getEventSports,
	getFutureEvents,
} from "../../utils/helper";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import SignInToCreateLeagueModal from "./Components/SignInToCreateLeagueModal";
import CreateTeamModal from "./Components/CreateTeamModal";
import {
	setLeaderboardLoading,
	setSelectedDiscipline,
} from "../../store/slices/fantasySlice";
import FantasyFiltersBar from "./Components/Filters/FantasyFiltersBar";
import HomepageLeaderBoard from "../HomePage/components/HomepageLeaderBoard";
import NextEventCard from "../HomePage/components/NextEventCard";
import {
	getGlobalLeaguesThunk,
	getLeaderboardThunk,
} from "../../store/thunks/fantasyThunk";
import ThreebySixAd from "../Ads/ThreebySixAd";
import { setIsMobile } from "../../store/slices/userSlice";
import ThreebyThreeAd from "../Ads/ThreebyThreeAd";
import FivebyNinetyAd from "../Ads/FivebyNinetyAd";
import ThreebyNinetyAd from "../Ads/ThreebyNinetyAd";
import FantasyTeam from "./Components/FantasyTeam";
import { getAdBySize } from "../../services/adService";

const FantasyPage = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		events,
		series,
		sports,
		selectedEvent,
		selectedSport,
		selectedSeries,
		selectedSeason,
		selectedLeague,
		selectedLeaderBoard,
	} = useAppSelector((state: RootState) => state.fantasy);

	const { isMobile, isLoggedIn } = useAppSelector(
		(state: RootState) => state.user,
	);

	const [teams, setTeams] = useState<TeamComplete[]>([]);
	const [teamLoading, setTeamLoading] = useState(false);
	const [leagues, setLeagues] = useState<LeagueWithSportsAndSeries[]>([]);
	const [userLeagues, setUserLeagues] = useState<LeagueWithSportsAndSeries[]>(
		[],
	);
	const [showCreateLeague, setShowCreateLeague] = useState(false);
	const [showCreateTeam, setShowCreateTeam] = useState(false);
	const [showMustSignIn, setShowMustSignIn] = useState(false);
	const [viewLeaderBoard, setViewLeaderboard] = useState(false);
	const [leaderboardLoading, setLeaderboardLoading] = useState(false);
	const [event, setEvent] = useState<Event>(initialEvent);
	const [defaultActiveKey, setDefaultActiveKey] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [hasSideLeagueAd, setHasSideLeagueAd] = useState(true);

	const filteredEvents = filterEventsBySportAndSeries(
		events,
		selectedSeries ?? undefined,
		selectedSport ?? undefined,
	);

	const futureEvents: Event[] = getFutureEvents(filteredEvents);

	const featuredLeagues = leagues.filter((league) => league.featured);

	// @ts-ignore
	// futureEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
	// const nearestFutureEvent = futureEvents[0];

	useEffect(() => {
		if (selectedSeason?._id) {
			const eventsForSportAndSeries = events.filter(
				(event) =>
					(selectedSport ? event.sport._id === selectedSport._id : true) &&
					(selectedSeries ? event.series._id === selectedSeries._id : true),
			);

			if (eventsForSportAndSeries.length > 0) {
				fetchTeams();
				fetchLeagues();
				fetchUserLeagues();
			}
			// dispatch(setSelectedEvent(nearestFutureEvent));
		}
	}, [selectedSport, selectedSeries, selectedSeason, isLoggedIn]);

	useEffect(() => {
		const handleResize = () => {
			dispatch(setIsMobile(window.innerWidth <= 768));
		};

		handleResize(); // Initial check
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		let isCancelled = false;

		const fetchSideLeagueAdAvailability = async () => {
			try {
				const adSize = isMobile ? "threebythree" : "threebysix";
				const response = await getAdBySize(adSize);
				const data = response?.data;
				if (isCancelled) {
					return;
				}
				setHasSideLeagueAd(Boolean(data?.imageUrl));
			} catch (error) {
				if (isCancelled) {
					return;
				}
				setHasSideLeagueAd(false);
			}
		};

		fetchSideLeagueAdAvailability();

		return () => {
			isCancelled = true;
		};
	}, [isMobile]);

	const fetchTeams = async () => {
		try {
			// debugger;
			setTeamLoading(true);
			const params: { sport?: string; series?: string; season: string } = {
				season: selectedSeason?._id ?? "",
			};

			if (selectedSport) params.sport = selectedSport?._id;
			if (selectedSeries) params.series = selectedSeries?._id;

			const res = await getTeams(params);
			setTeams(res.data);
		} catch (error) {
			console.error("Error fetching teams:", error);
			setTeams([]);
		} finally {
			setTeamLoading(false);
		}
	};

	const fetchLeagues = async () => {
		try {
			// Fetch leagues based on the selected sport/series
			const params: { sport?: string; series?: string; season?: string } = {};
			if (selectedSport) params.sport = selectedSport._id;
			if (selectedSeries) params.series = selectedSeries._id;
			if (selectedSeason) params.season = selectedSeason._id;

			const res = await getLeagues(params);
			//filter so that leagues with filtered = true appear first:

			const sorted = res.data.sort((a: any, b: any) =>
				a.filtered === b.filtered ? 0 : a.filtered ? -1 : 1,
			);
			setLeagues(sorted);
		} catch (error) {
			console.error("Error fetching leagues:", error);
		}
	};

	const fetchUserLeagues = async () => {
		try {
			// Fetch leagues based on the selected sport/series
			const params: { sport?: string; series?: string; season?: string } = {};
			if (selectedSport) params.sport = selectedSport._id;
			if (selectedSeries) params.series = selectedSeries._id;
			if (selectedSeason) params.season = selectedSeason._id;

			const res = await getUserLeagues(params);
			const sorted = res.data.sort((a: any, b: any) =>
				a.filtered === b.filtered ? 0 : a.filtered ? -1 : 1,
			);

			setUserLeagues(sorted);
		} catch (error) {
			console.error("Error fetching leagues:", error);
		}
	};

	useEffect(() => {
		// Update defaultActiveKey based on leagues data
		if (leagues.some((league) => league.image || league.roundImage)) {
			setDefaultActiveKey("prizes");
		} else {
			setDefaultActiveKey("countdown");
		}
	}, [leagues]);

	const handleCreateLeague = async (leagueData: CreateLeague) => {
		try {
			if (!isLoggedIn) {
				setShowMustSignIn(true);
				return;
			}

			const res = await createLeague(leagueData);
			if (res.status === 200 || res.status === 201) {
				fetchLeagues();
				fetchUserLeagues();
				setShowCreateLeague(false);
				const league = res.data;
				navigate(`/league/${league._id}`);
			} else {
				// Handle other status codes
				console.log(
					"Error creating league: Unexpected status code",
					res.status,
				);
			}
		} catch (error: any) {
			console.error("Error creating league:", error);
			if (
				error.response &&
				(error.response.status === 400 || error.response.status === 401)
			) {
				setShowCreateLeague(false);
				alert("Could not create league");

				// setShowMustSignIn(true);
			}
		}
	};

	useEffect(() => {
		if (selectedLeague && selectedLeague?._id) {
			dispatch(getLeaderboardThunk(selectedLeague._id));
		}
	}, [selectedLeague]);

	useEffect(() => {
		const fetchLeagues = async () => {
			try {
				setLeaderboardLoading(true);
				await dispatch(
					getGlobalLeaguesThunk({
						sportId: selectedSport?._id ?? "",
						seriesId: selectedSeries?._id ?? "",
						seasonId: selectedSeason?._id ?? "",
					}),
				);
			} catch (error) {
				console.error("Error fetching global leagues:", error);
			} finally {
				setLeaderboardLoading(false);
			}
		};

		fetchLeagues();
	}, [selectedSport, selectedSeries, selectedSeason]);

	const handleEditTeam = () => {
		//check if user is logged in
		try {
			const userId = localStorage.getItem("userId");
			if (!userId) {
				setShowModal(true);
				return;
			}
			// dispatch(setSelectedSeries(event.series));k
			// dispatch(setSelectedSport(event.sport));

			navigate(
				`/editteam?sport=${selectedSport._id}&series=${selectedSeries._id}`,
			);
		} catch (error) {
			console.error("Error handling pick team:", error);
		}
	};

	const handleCreateTeam = async (sport: Sport, series: Series) => {
		try {
			const res = await getTeams({ sport: sport._id, series: series._id });
			dispatch(setSelectedDiscipline({ sport, series }));
			navigate(`/editteam?sport=${sport._id}&series=${series._id}`);
		} catch (error: any) {
			console.error("Error fetching teams:", error);
			if (
				error.response &&
				(error.response.status === 400 || error.response.status === 401)
			) {
				setShowCreateTeam(false);
				setShowMustSignIn(true);
			}
		}
	};

	return (
		<Container className="fantasy-page" style={{ overflow: "hidden" }}>
			<Helmet>
				<title>Fantasy Homepage</title>
				<meta
					name="description"
					content="The Race Companion Fantasy Homepage. Fantasy leagues for Enduro, Downhill and Slopestyle mountain biking.  Follow along with Crankworx, UCI Downhill & Enduro, Red Bull Hardline and Red Bull Rampage."
				/>
				<meta name="viewport" content="width=device-width, initial-scale=1" />
			</Helmet>

			<FantasyFiltersBar className="pb-3 pt-3" />
			<div className="row pb-5 pt-2 justify-content-center">
				{selectedEvent && selectedEvent?._id && (
					<div className="col-lg-6 col-12">
						<NextEventCard event={selectedEvent} />
						{teams.length > 0 && (
							<>
								<Row className="justify-content-md-start justify-content-center mb-2">
									<Col xs={12} md={12} className="text-center">
										<Button
											variant="success"
											className="w-100 fw-bold border-2"
											onClick={handleEditTeam}
										>
											<MdPlaylistAdd className="me-2 mb-1" />
											EDIT TEAM
										</Button>
									</Col>
								</Row>
								<Row className="justify-content-md-start justify-content-center mb-2 mt-0 pt-0">
									<Col xs={12} md={12} className="text-center">
										<Button
											variant="outline-success"
											className="homepage-create-league-btn w-100 fw-bold border-2"
											onClick={() => setShowCreateLeague(true)}
										>
											<FaUsers className="me-2 mb-1" />
											CREATE LEAGUE
										</Button>
									</Col>
								</Row>
								<Row className="justify-content-md-start justify-content-center mb-2">
									<Col xs={12} md={12} className="text-center">
										<Button
											variant="outline-success"
											className="w-100 fw-bold border-2"
											onClick={() => setShowCreateTeam(true)}
										>
											<MdPlaylistAdd className="me-2 mb-1" />
											CREATE TEAM
										</Button>
									</Col>
								</Row>
							</>
						)}
					</div>
				)}
				<div className="col-md-9 col-lg-6 col-12 justify-content-center">
					{isMobile ? <ThreebyNinetyAd /> : <FivebyNinetyAd />}
					{teamLoading ? (
						<Placeholder as="div" animation="glow" className="my-5 mx-3">
							<Placeholder xs={8} bg="light" className="rounded-3 mb-2" />
							<Placeholder
								xs={11}
								className="rounded-3"
								bg="light"
								style={{ height: "13rem" }}
							/>
						</Placeholder>
					) : (
						<Container className="mt-3">
							{teams.length > 0 ? (
								<FantasyTeam
									team={teams[0]}
									isLoading={teamLoading}
									showLeagues={true}
									showLeagueSeries={false}
									showLeagueSport={false}
								/>
							) : (
								<>
									<Row className="justify-content-md-end">
										<Col xs={12} md={6} className="text-center">
											<Button
												variant="outline-light"
												className="homepage-create-league-btn w-100 fw-bold border-2 mb-md-0 mb-2"
												onClick={() => setShowCreateLeague(true)}
											>
												<FaUsers className="me-2 mb-1" />
												CREATE LEAGUE
											</Button>
										</Col>
										<Col xs={12} md={6} className="text-center">
											<Button
												variant="success"
												className="me-3 w-100 fw-bold mb-3"
												onClick={() => setShowCreateTeam(true)}
											>
												<MdPlaylistAdd className="me-2 mb-1" />
												CREATE TEAM 
											</Button>
										</Col>
									</Row>
									<HomepageLeaderBoard
										leaderboard={selectedLeaderBoard}
										leaderBoardLoading={leaderboardLoading}
									/>
								</>
							)}
						</Container>
					)}
				</div>
			</div>
			<Row className="d-flex flex-row justify-content-between">
				{hasSideLeagueAd && (
					<Col xs={12} md={2} lg={3} className="mb-3 mt-5">
						{isMobile ? <ThreebyThreeAd /> : <ThreebySixAd />}
					</Col>
				)}
				<Col
					xs={12}
					md={hasSideLeagueAd ? 8 : 12}
					lg={hasSideLeagueAd ? 9 : 12}
				>
					{featuredLeagues.length > 0 && (
						<FeaturedLeaguesComponent featuredLeagues={featuredLeagues} />
					)}
					<Leagues
						allLeagues={leagues}
						userLeagues={userLeagues}
						sports={sports}
						series={series}
					/>
					{event && event?._id && <EventCard event={event} />}
				</Col>
			</Row>
			<Container className="pt-2 fantasy-page__modals">
				<CreateLeagueModal
					show={showCreateLeague}
					handleClose={() => setShowCreateLeague(false)}
					events={events}
					season={selectedSeason}
					sports={getEventSports(futureEvents)}
					seriesList={getEventSeries(futureEvents)}
					handleCreateLeague={handleCreateLeague}
				/>
				<SignInToCreateLeagueModal
					show={showMustSignIn}
					onHide={() => setShowMustSignIn(false)}
				/>
				<CreateTeamModal
					showCreateTeam={showCreateTeam}
					setShowCreateTeam={setShowCreateTeam}
					handleCreateTeam={handleCreateTeam}
				/>
			</Container>
			<Modal
				show={showModal}
				onHide={() => setShowModal(false)}
				// size=''
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>
						<FiLogIn className="mb-1 me-2" />
						Sign In
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>You must be signed in to pick your team.</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="primary"
						onClick={() => {
							navigate("/login");
						}}
					>
						Sign In
					</Button>
				</Modal.Footer>
			</Modal>
		</Container>
	);
};

export default FantasyPage;
